import { useEffect, useState, type SubmitEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AuthMethodsResponseSchema,
  RecoveryCodesResponseSchema,
  SetupLinkInfoSchema,
  SetupPasswordResponseSchema,
  type AuthenticatorEnrolment,
} from '@taqon/contracts';
import { useLocation, useNavigate } from 'react-router';
import { ApiError, getJson } from '../../shared/api/client';
import { postAuth } from './auth-api';
import {
  AuthCard,
  AuthenticatorDetails,
  CodeField,
  PasswordField,
  RecoveryCodes,
} from './AuthCard';

/**
 * Account setup through an administrator's one-time link (`/account-setup#token=…`; SDD §11.1.2,
 * FRD-USR-025): the user chooses their own password, adds IBMS to an authenticator app, confirms
 * the first code, and receives recovery codes once. Where the second factor is not required
 * (CLR-42), choosing the password is the whole setup.
 *
 * The token is read from the URL **fragment**, which browsers never send to a server, and is then
 * removed from the address bar. It travels to the API only in request bodies. Rendered before the
 * session check, so it works signed out.
 */
export function AccountSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token') ?? '');

  useEffect(() => {
    // Out of the address bar and the history entry, whatever happens next.
    if (location.hash !== '') void navigate('/account-setup', { replace: true });
  }, [location.hash, navigate]);

  const methods = useQuery({
    queryKey: ['auth-methods'],
    retry: false,
    queryFn: () => getJson('/auth/methods', AuthMethodsResponseSchema),
  });
  const csrf = methods.data?.csrfToken ?? '';
  const link = useQuery({
    queryKey: ['account-setup', token],
    enabled: token !== '' && csrf !== '',
    retry: false,
    queryFn: () => postAuth('/auth/setup/inspect', { token }, SetupLinkInfoSchema, csrf),
  });

  if (token === '') {
    return (
      <AuthCard heading="Set up your sign-in">
        <InvalidLink />
      </AuthCard>
    );
  }
  if (methods.isPending || (csrf !== '' && link.isPending)) {
    return (
      <AuthCard heading="Set up your sign-in">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Checking the setup link" />
        </Box>
      </AuthCard>
    );
  }
  if (methods.data?.password !== true) {
    return (
      <AuthCard heading="Set up your sign-in">
        <Alert severity="error" variant="outlined">
          Sign-in setup is not available on this system. Contact your administrator.
        </Alert>
      </AuthCard>
    );
  }
  if (link.isError || link.data === undefined) {
    return (
      <AuthCard heading="Set up your sign-in">
        {link.error instanceof ApiError && link.error.status === 404 ? (
          <InvalidLink />
        ) : (
          <Alert severity="error" variant="outlined">
            {link.error?.message ?? 'The setup link could not be checked.'}
          </Alert>
        )}
      </AuthCard>
    );
  }
  return (
    <AuthCard heading={`Set up your sign-in, ${link.data.displayName}`}>
      <SetupSteps
        token={token}
        csrf={csrf}
        email={link.data.email}
        secondFactor={link.data.secondFactor}
      />
    </AuthCard>
  );
}

function InvalidLink() {
  return (
    <Stack spacing={2}>
      <Alert severity="error" variant="outlined">
        This setup link is not valid. It may have expired, been used already, or been replaced by a
        newer one. Ask your administrator for a new link.
      </Alert>
      <Button href="/" variant="outlined">
        Go to sign in
      </Button>
    </Stack>
  );
}

type Stage =
  | { kind: 'password' }
  | { kind: 'authenticator'; enrolment: AuthenticatorEnrolment }
  | { kind: 'done'; recoveryCodes: readonly string[] };

function SetupSteps({
  token,
  csrf,
  email,
  secondFactor,
}: {
  token: string;
  csrf: string;
  email: string;
  secondFactor: boolean;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'password' });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');

  const choosePassword = useMutation({
    mutationFn: () =>
      postAuth('/auth/setup/password', { token, password }, SetupPasswordResponseSchema, csrf),
    onSuccess: (answer) => {
      setPassword('');
      setRepeat('');
      if (answer === undefined) return;
      // No authenticator to add: the password finished the setup, and there are no recovery codes.
      if ('complete' in answer) setStage({ kind: 'done', recoveryCodes: [] });
      else setStage({ kind: 'authenticator', enrolment: answer });
    },
  });
  const confirm = useMutation({
    mutationFn: () =>
      postAuth('/auth/setup/confirm', { token, code }, RecoveryCodesResponseSchema, csrf),
    onSuccess: (result) => {
      setCode('');
      if (result !== undefined) setStage({ kind: 'done', recoveryCodes: result.recoveryCodes });
    },
    onError: () => {
      setCode('');
    },
  });

  if (stage.kind === 'password') {
    const mismatch = repeat !== '' && repeat !== password;
    return (
      <Box
        component="form"
        noValidate
        aria-label="Choose a password"
        onSubmit={(event: SubmitEvent<HTMLFormElement>) => {
          event.preventDefault();
          choosePassword.mutate();
        }}
      >
        <Stack spacing={2.5}>
          <Typography variant="body2">
            {secondFactor ? 'Step 1 of 2: choose' : 'Choose'} a password for{' '}
            <strong>{email}</strong>. Nobody else, including your administrator, will see it.
          </Typography>
          {choosePassword.isError && (
            <Alert severity="error" variant="outlined">
              {choosePassword.error.message}
            </Alert>
          )}
          <input type="email" autoComplete="username" value={email} readOnly hidden />
          <PasswordField
            label="New password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
          <PasswordField
            label="Repeat the password"
            autoComplete="new-password"
            required
            value={repeat}
            error={mismatch}
            helperText={mismatch ? 'The passwords are not the same.' : ' '}
            onChange={(e) => {
              setRepeat(e.target.value);
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disableElevation
            disabled={choosePassword.isPending || password === '' || repeat !== password}
          >
            {choosePassword.isPending ? 'Saving…' : secondFactor ? 'Continue' : 'Finish setup'}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (stage.kind === 'authenticator') {
    return (
      <Box
        component="form"
        noValidate
        aria-label="Add the authenticator"
        onSubmit={(event: SubmitEvent<HTMLFormElement>) => {
          event.preventDefault();
          confirm.mutate();
        }}
      >
        <Stack spacing={2.5}>
          <Typography variant="body2">
            Step 2 of 2: scan this code with an authenticator app (for example Microsoft
            Authenticator or Google Authenticator), then enter the 6-digit code it shows.
          </Typography>
          <AuthenticatorDetails enrolment={stage.enrolment} />
          {confirm.isError && (
            <Alert severity="error" variant="outlined">
              {confirm.error.message}
            </Alert>
          )}
          <CodeField autoFocus value={code} onCode={setCode} />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disableElevation
            disabled={confirm.isPending || code.length !== 6}
          >
            {confirm.isPending ? 'Checking…' : 'Finish setup'}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Alert severity="success" variant="outlined">
        {stage.recoveryCodes.length > 0
          ? 'Your sign-in is ready. From now on, sign in with your email, your password and a code from your authenticator app.'
          : 'Your sign-in is ready. From now on, sign in with your email and your password.'}
      </Alert>
      {stage.recoveryCodes.length > 0 && <RecoveryCodes codes={stage.recoveryCodes} />}
      <Button href="/" variant="contained" size="large" disableElevation>
        Go to sign in
      </Button>
    </Stack>
  );
}
